import { createStore, del, entries, get, set } from 'idb-keyval'

import type { PendingMission } from './types'

/**
 * IndexedDB façade for the offline queue.
 *
 * Why `idb-keyval` rather than the lower-level `idb`:
 *   - 2 KB gzip, zero ceremony.
 *   - Our access pattern is purely key-value (one record per
 *     mission, looked up by `localId`); we don't need cursors,
 *     compound indexes, or schema migrations.
 *   - When/if we outgrow this (e.g. a "synced missions cache"
 *     that needs `IDBKeyRange` queries), we'll graduate to `idb`
 *     and keep this module as a façade.
 *
 * The DB name is namespaced to the app (`avity-offline`) so it
 * can't collide with anything else the user might install on the
 * same origin. The single object store holds full
 * `PendingMission` records (Blobs included — IDB stores them
 * natively, no base64 round-trip).
 *
 * NB: Module-level `createStore` immediately calls
 * `window.indexedDB.open`. That's fine in a Vite-built PWA, but
 * means this file MUST stay out of any import chain reachable
 * from a non-DOM context (e.g. server-side rendering).
 */

const DB_NAME = 'avity-offline'
const STORE_NAME = 'pending-missions'

const pendingMissionsStore = createStore(DB_NAME, STORE_NAME)

/** Append (or overwrite by `localId`) a queued mission. */
export async function putPendingMission(mission: PendingMission): Promise<void> {
  await set(mission.localId, mission, pendingMissionsStore)
}

/** Fetch a single queued mission by its local id. */
export async function getPendingMission(localId: string): Promise<PendingMission | undefined> {
  return get<PendingMission>(localId, pendingMissionsStore)
}

/**
 * Read every pending mission, oldest first.
 *
 * Sorted by `createdAt` (epoch ms set when the driver tapped
 * "Envoyer") so the sync walks the queue in arrival order — first
 * mission filed offline is first to be uploaded when the network
 * returns. UUID v7 keys would also sort chronologically, but
 * `createdAt` survives any future key-format change.
 */
export async function listPendingMissions(): Promise<PendingMission[]> {
  const rows = await entries<string, PendingMission>(pendingMissionsStore)
  return rows
    .map(([, value]) => value)
    .sort((a, b) => a.createdAt - b.createdAt)
}

/** Drop a mission from the queue (after a successful upload). */
export async function removePendingMission(localId: string): Promise<void> {
  await del(localId, pendingMissionsStore)
}

/** Light helper for the header badge / sync store. */
export async function countPending(): Promise<{ pending: number; error: number }> {
  const all = await listPendingMissions()
  return {
    pending: all.filter((m) => m.status !== 'error').length,
    error: all.filter((m) => m.status === 'error').length,
  }
}
